import { connectInternalApi, createApi, createAxiosInstance } from 'common/api'

export type Region = 'EU' | 'US' | 'UK'

type ContentResponse = {
  items: {
    id: string
    name: string
    account: { id: string; name: string }
    createdDate: string
    isActive: boolean
    region: Region
    updatedDate: string
    defaultStatus: 'New Combination' | 'Inactive'
  }[]
}

type DetailsResponse = {
  items: {
    personalizationId: string
    generatedVariants: number
    status: string
  }[]
}

export type PersonalizationContentResponse = {
  id: string
  account: { id: string; name: string }
  createdDate: string
  isActive: boolean
  name: string
  region: Region
  updatedDate: string
  personalizationId: string
  generatedVariants: number
  status: string
  defaultStatus: 'New Combination' | 'Inactive'
}[]

export type CombinationLibraryEntryStatus =
  | 'New combination'
  | 'Awaiting approval'
  | 'Live'
  | 'Dropped'

export type CombinationLibraryResponse = {
  items: {
    id: string
    channel: string
    campaignType: string
    productItem: string
    productCategory: string
    status: CombinationLibraryEntryStatus
    dimensions: { key: string; value: string }[]
  }[]
}

const URL = process.env.REACT_APP_PERSONALIZATION_LANGUAGE
if (!URL) {
  throw new Error('REACT_APP_PERSONALIZATION_LANGUAGE is not defined')
}

const parsedUrls: { [key: string]: string } = JSON.parse(URL)

const regionalLanguageReportingUrls = Object.fromEntries(
  Object.entries(parsedUrls).map(([key, value]) => [
    key,
    createApi(createAxiosInstance(value)),
  ])
)

export async function fetchContent(): Promise<PersonalizationContentResponse> {
  const response = await connectInternalApi.get<ContentResponse>(
    `v1/accounts/personalization/library`
  )

  const regions = [
    ...new Set(response.data.items.map((item) => item.region.toLowerCase())),
  ]
  const urlPerRegion = JSON.parse(URL ?? '')
  const details = await Promise.all(
    regions.map((region) =>
      connectInternalApi.get<DetailsResponse>(
        `${urlPerRegion[region.toLowerCase()]}/accounts/personalization/details`
      )
    )
  )
  const flattenedDetails = details.flatMap(
    (detailsResponse) => detailsResponse.data.items
  )
  return response.data.items.map((item) => ({
    ...item,
    ...(flattenedDetails.find(
      (detail) => detail.personalizationId === item.id
    ) ?? {
      personalizationId: '',
      generatedVariants: 0,
      status: item.defaultStatus,
    }),
  }))
}

export async function fetchCombinations({
  accountId,
  campaignId,
  region,
}: {
  accountId: string
  campaignId: string
  region: Region | undefined
}): Promise<CombinationLibraryResponse> {
  if (!region) {
    throw new Error('Region is not defined')
  }

  if (!regionalLanguageReportingUrls[region.toLowerCase()]) {
    throw new Error(`Region ${region} is not supported`)
  }

  const response = await regionalLanguageReportingUrls[
    region.toLowerCase()
  ].get<CombinationLibraryResponse>(
    // hardcoding the accountId and personalizationId, until the BE is providing legit data
    `/accounts/66b4a3de18f2140007889500/personalization/66d17c9e50227ec2a52c214d/combinations`
  )
  return response.data
}
